import { GroupShapeIcon } from "@/components/shapes/GroupShapeIcon";
import { groupColors } from "@/configs/map/groups";
import { ActionIcon, Flex, Paper, Text } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import type { Feature } from "geojson";
import React, { useMemo } from "react";
import "./MapTooltip.css";

export function MapTooltip({
  feature,
  position,
  categoryConfigs,
  categoryColors,
  activeDataflowId,
  onEditGroup,
}: {
  feature: Feature | null;
  position: { x: number; y: number } | null;
  categoryConfigs?: Record<
    string,
    {
      name?: string;
      color?: string;
      shape?: "circle" | "square" | "triangle" | "star" | "diamond";
    }
  >;
  categoryColors?: Map<string, string> | Record<string, string>;
  activeDataflowId?: string | null;
  onEditGroup?: (groupValue: string, groupColor: string) => void;
}) {
  const properties = useMemo(() => {
    if (!feature?.properties) return null;

    // Filter out internal properties that start with _
    const props = { ...feature.properties };
    const filteredProps: Record<string, any> = {};

    Object.keys(props).forEach((key) => {
      if (!key.startsWith("_")) {
        filteredProps[key] = props[key];
      }
    });

    return filteredProps;
  }, [feature]);

  // Get group information for display row
  const groupInfo = useMemo(() => {
    const category = feature?.properties?._category;
    if (!category || typeof category !== "string") return null;

    const config = categoryConfigs?.[category];

    // Get color from config or use default from categoryColors
    // categoryColors is a Map or Record with default colors based on sorted order
    let defaultColor = groupColors[0];
    if (categoryColors) {
      if (categoryColors instanceof Map) {
        defaultColor = categoryColors.get(category) || groupColors[0];
      } else {
        defaultColor = categoryColors[category] || groupColors[0];
      }
    }

    const configColor = config?.color;
    
    // Get shape from config, or from feature properties (stored during layer creation), or default to circle
    // Feature properties may have _shape stored directly from layer creation
    const shapeFromProps = feature?.properties?._shape;
    const shape = config?.shape || shapeFromProps || "circle";
    
    // Get color from feature properties if available (computed during layer creation)
    const colorFromProps = feature?.properties?._computedColor;
    const finalColor = (configColor && configColor.trim() !== "") 
      ? configColor 
      : (colorFromProps || defaultColor);
    
    const name = config?.name || category;

    return { category, name, color: finalColor, shape };
  }, [feature?.properties?._category, feature?.properties?._shape, feature?.properties?._computedColor, categoryConfigs, categoryColors]);

  if (!feature || !position || !properties) {
    return null;
  }

  return (
    <div
      className="map-tooltip"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
        pointerEvents: "auto", // Enable pointer events to prevent clicks from closing tooltip
        zIndex: 10,
      }}
      onClick={(e) => {
        // Prevent clicks inside tooltip from propagating to map
        e.stopPropagation();
      }}
    >
      <div className="map-tooltip-content">
        <Paper
          shadow="md"
          p="sm"
          style={{
            background: "transparent",
            boxShadow: "none",
          }}
        >
          {groupInfo && (
            <Flex align="center" gap="xs" className="map-tooltip-value" mb="sm">
              <GroupShapeIcon
                shape={groupInfo.shape}
                color={groupInfo.color}
                size={16}
                filled={true}
              />
              <Text truncate size="sm" fw={600}>
                {groupInfo.name}
              </Text>
              {activeDataflowId && onEditGroup && (
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditGroup(groupInfo.category, groupInfo.color);
                  }}
                  style={{ flexShrink: 0 }}
                >
                  <IconPencil size={14} />
                </ActionIcon>
              )}
            </Flex>
          )}
          <div className="map-tooltip-grid" style={{ paddingBottom: "28px" }}>
            {Object.entries(properties).map(([key, value]) => {
              // Format the value for display
              let displayValue = value;
              if (value === null || value === undefined) {
                displayValue = "—";
              } else if (typeof value === "object") {
                displayValue = JSON.stringify(value);
              } else {
                displayValue = String(value);
              }

              return (
                <React.Fragment key={key}>
                  <Text
                    size="sm"
                    fw={600}
                    c="gray.9"
                    className="map-tooltip-label"
                  >
                    {key}
                  </Text>
                  <Text size="sm" className="map-tooltip-value">
                    {displayValue}
                  </Text>
                </React.Fragment>
              );
            })}
          </div>
        </Paper>
      </div>
    </div>
  );
}
